import { ref, computed } from 'vue'
import type { Category } from './category.service'

export function useCategoryStoreBase() {
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const categoriesById = ref(new Map<string, Category>())
  const categoriesByPath = ref(new Map<string, Category>())

  const allCategories = computed(() => Array.from(categoriesById.value.values()))

  function get(id: string): Category | undefined {
    return categoriesById.value.get(id)
  }

  function getParentsByPath(path: string): Category[] {
    const result: Category[] = []
    if (!path) return result
    const segments = path.split('.')
    for (let i = 1; i <= segments.length; i++) {
      const ancestorPath = segments.slice(0, i).join('.')
      const category = categoriesByPath.value.get(ancestorPath)
      if (category) result.push(category)
    }
    return result
  }

  function getAll({ depth, parentId }: { depth?: number; parentId?: string } = {}): Category[] {
    if (depth === undefined && parentId === undefined) return allCategories.value
    return allCategories.value.filter((cat) => {
      const parentMatch = parentId === undefined || cat.parentId === parentId
      const depthMatch = depth === undefined || cat.level === depth
      return parentMatch && depthMatch
    })
  }

  function cascadeDescendantPaths(
    newById: Map<string, Category>,
    newByPath: Map<string, Category>,
    oldPath: string,
    newPath: string,
    excludeId: string
  ) {
    for (const [descId, desc] of newById) {
      if (desc.id === excludeId || !desc.path?.startsWith(oldPath + '.')) continue
      const descNewPath = newPath + desc.path.slice(oldPath.length)
      const descSegments = descNewPath.split('.')
      const updated: Category = {
        ...desc,
        path: descNewPath,
        level: descSegments.length,
        parentId: descSegments.length > 1 ? descSegments[descSegments.length - 2] : undefined
      }
      newById.set(descId, updated)
      newByPath.delete(desc.path)
      newByPath.set(descNewPath, updated)
    }
  }

  function removeCategorySubtree(id: string) {
    const existing = categoriesById.value.get(id)
    if (!existing) return

    const newById = new Map(categoriesById.value)
    const newByPath = new Map(categoriesByPath.value)

    for (const [catId, cat] of newById) {
      if (catId === id || (existing.path && cat.path?.startsWith(existing.path + '.'))) {
        newById.delete(catId)
        if (cat.path) newByPath.delete(cat.path)
      }
    }

    categoriesById.value = newById
    categoriesByPath.value = newByPath
  }

  function clearCache() {
    isLoading.value = false
    isLoaded.value = false
    categoriesById.value = new Map()
    categoriesByPath.value = new Map()
  }

  return {
    isLoading,
    isLoaded,
    categoriesById,
    categoriesByPath,
    allCategories,
    get,
    getParentsByPath,
    getAll,
    cascadeDescendantPaths,
    removeCategorySubtree,
    clearCache
  }
}
